import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit
} from '@angular/core';
import {Subject, fromEvent} from 'rxjs';
import {map, switchMap, takeUntil} from 'rxjs/operators';
import {PopupManagerService} from '../../../core/services/popup-manager.service';

@Directive({
  selector: '[appResizableDialog]',
  standalone: true
})
export class ResizableDialogDirective implements AfterViewInit, OnDestroy, OnInit {
  @Input() resizableKey: string;
  @Input() resizableTargetClass: string;
  private target: HTMLElement;
  private handleContainer: HTMLElement;
  private handle: HTMLElement;
  private delta = {x: 0, y: 0};
  private startSize = [0, 0];

  private destroy$ = new Subject<void>();

  constructor(
    private elementRef: ElementRef,
    private zone: NgZone,
    private popupSvc: PopupManagerService
  ) {}

  ngOnInit() {
    if (this.popupSvc.sizes[this.resizableKey]) {
      this.startSize = this.popupSvc.sizes[this.resizableKey];
    }
  }

  public ngAfterViewInit(): void {
    const additionalSelector = this.resizableTargetClass ? `.${this.resizableTargetClass}` : '';

    this.target = document.querySelector(`.q9-dialog${additionalSelector}`) as HTMLElement;
    this.generateHandle();

    if (!this.popupSvc.sizes[this.resizableKey]) {
      this.startSize = [this.target.offsetWidth, this.target.offsetHeight];
    }

    this.setupEvents();
    this.translate();
  }

  public ngOnDestroy(): void {
    this.popupSvc.sizes[this.resizableKey] = [this.target.offsetWidth, this.target.offsetHeight];
    this.destroy$.next();
  }

  private setupEvents() {
    this.zone.runOutsideAngular(() => {
      const mousedown$ = fromEvent(this.handle, 'mousedown');
      const mousemove$ = fromEvent(document, 'mousemove');
      const mouseup$ = fromEvent(document, 'mouseup');
      const mousedrag$ = mousedown$.pipe(
        takeUntil(this.destroy$),
        switchMap((event: MouseEvent) => {
          const startX = event.clientX;
          const startY = event.clientY;

          this.startSize = [this.target.offsetWidth, this.target.offsetHeight];

          return mousemove$.pipe(
            takeUntil(mouseup$),
            map((e: MouseEvent) => {
              e.preventDefault();
              this.delta = {
                x: e.clientX - startX,
                y: e.clientY - startY
              };
            })
          );
        })
      );

      mousedrag$.subscribe(() => {
        if (this.delta.x === 0 && this.delta.y === 0) {
          return;
        }

        this.translate();
      });

      mouseup$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.delta = {x: 0, y: 0};
      });
    });
  }

  private translate() {
    requestAnimationFrame(() => {
      this.target.style.width = `${this.startSize[0] + this.delta.x}px`;
      this.target.style.height = `${this.startSize[1] + this.delta.y}px`;
    });
  }

  private generateHandle() {
    this.handle = document.createElement('div');
    const additionalSelector = this.resizableTargetClass ? `.${this.resizableTargetClass}` : '';

    this.handleContainer = document.querySelector(`.q9-dialog${additionalSelector}`);
    this.handle.style.position = 'absolute';
    this.handle.style.bottom = '0';
    this.handle.style.right = '0';
    this.handle.style.borderStyle = 'solid';
    this.handle.style.borderWidth = '0 0 20px 20px';
    this.handle.style.borderColor = 'transparent transparent var(--q9-accent-300) transparent';
    this.handle.style.cursor = 'nwse-resize';
    this.handle.style.zIndex = '999';
    this.handleContainer.appendChild(this.handle);
  }
}
